import { NgClass, NgIf, SlicePipe, NgFor, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ChangeDetectorRef,
    inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TeamService } from '../../shared/services/team.service';
import { Subject, takeUntil } from 'rxjs';
import { EditUserDialogComponent } from './edit-user-dialog/edit-user-dialog.component';
import { AuthService } from 'app/core/auth/auth.service';

@Component({
    selector: 'app-users-details',
    templateUrl: './users-details.component.html',
    styleUrl: './users-details.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatInputModule,
        MatFormFieldModule,
        MatPaginatorModule,
        MatMenuModule,
        MatDialogModule,
        NgClass,
        NgIf,
        NgFor,
        TitleCasePipe,
        FormsModule
    ],
})
export class UsersDetailsComponent implements OnInit, OnDestroy {
    usersDataSource: MatTableDataSource<any> = new MatTableDataSource();
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private currentRole: 'all' | 'player' | 'admin' = 'all';
    isLoading: boolean = true;
    
    // Search properties
    searchQuery: string = '';
    filteredUsers: any[] = [];
    showSearchDropdown: boolean = false;
    isSearchActive: boolean = false;
    selectedUser: any = null;
    
    // Pagination properties
    pageSize: number = 10;
    currentPage: number = 0;
    totalUsers: number = 0;
    pageSizeOptions: number[] = [10, 25, 50, 100];
    
    // Original users from API
    allUsers: any[] = [];
    
    // Stats properties
    superAdminCount: number = 0;
    adminCount: number = 0;
    managerCount: number = 0;
    playerCount: number = 0;
    emailVerifiedCount: number = 0;
    
    // Expanded rows - use an object to track expanded state
    expandedUsers: { [key: string]: boolean } = {};
    
    readonly dialog = inject(MatDialog);
    private readonly authService = inject(AuthService);
    readonly canPerformDestructiveActions = this.authService.isSuperAdmin();

    constructor(
          private teamsService: TeamService,
          private cdr: ChangeDetectorRef,
          private snackBar: MatSnackBar,
          private route: ActivatedRoute
    ) { }

    ngOnInit(): void {
        this.route.data.subscribe(data => {
            const role = data['role'];
            this.currentRole = role === 'player' || role === 'admin' ? role : 'all';
            this.currentPage = 0;
            this.loadCurrentView();
        });
    }
    currentViewTitle: string = 'All Users'; // default
currentViewDescription: string = 'View all users across the system';
    getPlayers(): void {
    this.currentViewTitle = 'All Players';
    this.currentViewDescription = 'View all players across the system';    
    this.isLoading = true;
    this.teamsService.getPlayers(this.pageRequest())
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
            next: (response) => this.processUsersResponse(response),
            error: (err) => this.handleUsersError(err)
        });
}

getAdmins(): void {
    this.currentViewTitle = 'All Admins';
    this.currentViewDescription = 'View all admins across the system';
    this.isLoading = true;
    this.teamsService.getAdmins(this.pageRequest())
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
            next: (response) => this.processUsersResponse(response),
            error: (err) => this.handleUsersError(err)
        });
}
private processUsersResponse(response: any): void {
    const usersArray = response?.results || [];

    if (!Array.isArray(usersArray)) {
        console.error('Response results is not an array:', usersArray);
        this.snackBar.open('Invalid data format from server', 'Close', { duration: 3000 });
        this.isLoading = false;
        this.cdr.markForCheck();
        return;
    }

    const usersForView = usersArray.filter(
        user => this.currentRole === 'all' || user.role === this.currentRole
    );

    this.expandedUsers = {};

    this.allUsers = usersForView.map(user => ({
        id: user._id,
                            _id: user._id,
                            name: user.name || 'No Name',
                            firstNameLetter: this.getFirstNameLetter(user.name),
                            email: user.email,
                            role: user.role,
                            profilePicture: user.picture || '',
                            isEmailVerified: user.isEmailVerified || false,
                            isPhoneVerified: user.isPhoneVerified || false,
                            profileType: user.profileType || 'public',
                            language: user.language || 'en',
                            country: user.country || 'N/A',
                            venues: user.manager?.venues || [],
                            friends: user.friends || [],
                            oauthProviders: user.oauthProviders || [],
                            companyName: user.companyId?.name || 'No Company', // Only store company name
                            firebaseToken: user.firebaseToken ? 'Available' : 'Not Available',
                            lastPasswordUpdate: user.lastUpdatedPasswordTimeStamp,
                            createdAt: user.createdAt,
                            updatedAt: user.updatedAt,
                            // Role badge styling
                            roleBadgeClass: this.getRoleBadgeClass(user.role),
                            roleDisplay: this.getRoleDisplayName(user.role),
                            // Background color based on first letter
                            avatarColor: this.getAvatarColor(user.name)
    }));

    this.calculateStats();
    this.filteredUsers = [...this.allUsers];
    this.totalUsers = Number(response?.totalResults) || 0;
    this.usersDataSource.data = [...this.filteredUsers];
    this.isLoading = false;
    this.cdr.markForCheck();
}

private handleUsersError(err: any): void {
    console.error('Error fetching users:', err);
    this.snackBar.open('Error loading users', 'Close', { duration: 3000 });
    this.isLoading = false;
    this.cdr.markForCheck();
}


    getAllUsers(): void { 
        this.currentViewTitle = 'All Users';
    this.currentViewDescription = 'View all users across the system';
        this.isLoading = true;
        this.teamsService.getUsers(this.pageRequest())
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    // Extract the results array from the response
                    const usersArray = response?.results || [];
                    
                    if (!Array.isArray(usersArray)) {
                        console.error('Response results is not an array:', usersArray);
                        this.snackBar.open('Invalid data format from server', 'Close', { duration: 3000 });
                        this.isLoading = false;
                        this.cdr.markForCheck();
                        return;
                    }
                    
                    // Reset expanded users when loading new data
                    this.expandedUsers = {};
                    
                    // Map the users array
                    this.allUsers = usersArray.map(user => {
                        const userData = {
                            id: user._id,
                            _id: user._id,
                            name: user.name || 'No Name',
                            firstNameLetter: this.getFirstNameLetter(user.name),
                            email: user.email,
                            phone: user.phone || '',
                            role: user.role,
                            profilePicture: user.picture || '',
                            isEmailVerified: user.isEmailVerified || false,
                            isPhoneVerified: user.isPhoneVerified || false,
                            profileType: user.profileType || 'public',
                            language: user.language || 'en',
                            country: user.country || 'N/A',
                            venues: user.manager?.venues || [],
                            friends: user.friends || [],
                            oauthProviders: user.oauthProviders || [],
                            companyName: user.companyId?.name || 'No Company',
                            firebaseToken: user.firebaseToken ? 'Available' : 'Not Available',
                            lastPasswordUpdate: user.lastUpdatedPasswordTimeStamp,
                            createdAt: user.createdAt,
                            updatedAt: user.updatedAt,
                            // Role badge styling
                            roleBadgeClass: this.getRoleBadgeClass(user.role),
                            roleDisplay: this.getRoleDisplayName(user.role),
                            // Background color based on first letter
                            avatarColor: this.getAvatarColor(user.name)
                        };
                        
                        // Initialize expanded state
                        this.expandedUsers[user._id] = false;
                        
                        return userData;
                    });
                    
                    this.calculateStats();
                    this.filteredUsers = [...this.allUsers];
                    this.totalUsers = Number(response?.totalResults) || 0;
                    this.usersDataSource.data = [...this.filteredUsers];
                    this.isLoading = false;
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error fetching users:', err);
                    this.snackBar.open('Error loading users', 'Close', { duration: 3000 });
                    this.isLoading = false;
                    this.cdr.markForCheck();
                }
            });
    }

    calculateStats(): void {
        this.superAdminCount = this.allUsers.filter(u => u.role === 'super-admin').length;
        this.adminCount = this.allUsers.filter(u => u.role === 'admin').length;
        this.managerCount = this.allUsers.filter(u => u.role === 'manager').length;
        this.playerCount = this.allUsers.filter(u => u.role === 'player').length;
        this.emailVerifiedCount = this.allUsers.filter(u => u.isEmailVerified).length;
    }

    onSearchInput(): void {
        if (this.searchQuery.trim() === '') {
            this.clearSearch();
            return;
        }

        const query = this.searchQuery.toLowerCase();
        this.filteredUsers = this.allUsers.filter(user => 
            user.name.toLowerCase().includes(query) || 
            user.email.toLowerCase().includes(query) ||
            user.role.toLowerCase().includes(query) ||
            user.country.toLowerCase().includes(query) ||
            user.profileType.toLowerCase().includes(query) ||
            (user.companyName?.toLowerCase() || '').includes(query)
        );
        
        this.showSearchDropdown = this.filteredUsers.length > 0;
        this.isSearchActive = false;
        this.selectedUser = null;
        
        this.resetExpandedUsers();
        
        this.currentPage = 0;
        this.totalUsers = this.filteredUsers.length;
        this.updateDisplayedUsers();
        
        this.cdr.markForCheck();
    }

    selectUser(user: any): void {
        this.selectedUser = user;
        this.searchQuery = user.name;
        this.showSearchDropdown = false;
        this.isSearchActive = true;
        
        this.resetExpandedUsers();
        this.expandedUsers[user._id] = true;
        
        this.filteredUsers = [user];
        this.currentPage = 0;
        this.totalUsers = 1;
        this.updateDisplayedUsers();
        
        setTimeout(() => {
            const element = document.getElementById(`user-${user._id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('bg-yellow-50');
                setTimeout(() => element.classList.remove('bg-yellow-50'), 2000);
            }
        }, 100);
        
        this.cdr.markForCheck();
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.selectedUser = null;
        this.showSearchDropdown = false;
        this.isSearchActive = false;
        
        this.resetExpandedUsers();
        this.currentPage = 0;
        this.loadCurrentView();
    }

    onSearchBlur(): void {
        setTimeout(() => {
            this.showSearchDropdown = false;
            this.cdr.markForCheck();
        }, 200);
    }

    private resetExpandedUsers(): void {
        Object.keys(this.expandedUsers).forEach(key => {
            this.expandedUsers[key] = false;
        });
    }

    isUserExpanded(userId: string): boolean {
        return !!this.expandedUsers[userId];
    }

    toggleRow(userId: string, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        
        this.expandedUsers[userId] = !this.expandedUsers[userId];
        
        if (this.expandedUsers[userId]) {
            Object.keys(this.expandedUsers).forEach(key => {
                if (key !== userId) {
                    this.expandedUsers[key] = false;
                }
            });
        }
        
        this.cdr.markForCheck();
    }

    updateDisplayedUsers(): void {
        const startIndex = this.currentPage * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.usersDataSource.data = this.filteredUsers.slice(startIndex, endIndex);
    }

    onPageChange(event: PageEvent): void {
        this.currentPage = event.pageIndex;
        this.pageSize = event.pageSize;
        this.resetExpandedUsers();
        this.loadCurrentView();
    }

    getPageInfo(): string {
        const start = (this.currentPage * this.pageSize) + 1;
        const end = Math.min((this.currentPage + 1) * this.pageSize, this.totalUsers);
        return `Showing ${start}-${end} of ${this.totalUsers} users`;
    }

    refreshUsers(): void {
        this.searchQuery = '';
        this.selectedUser = null;
        this.showSearchDropdown = false;
        this.isSearchActive = false;
        this.currentPage = 0;
        this.loadCurrentView();
        this.snackBar.open('Users refreshed', 'Close', { duration: 2000 });
    }

    private pageRequest(): { page: number; limit: number; sortBy: string } {
        return {
            page: this.currentPage + 1,
            limit: this.pageSize,
            sortBy: 'createdAt:desc',
        };
    }

    private loadCurrentView(): void {
        if (this.currentRole === 'player') {
            this.getPlayers();
        } else if (this.currentRole === 'admin') {
            this.getAdmins();
        } else {
            this.getAllUsers();
        }
    }

    formatDateShort(dateString: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    formatDate(dateString: string): string {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    private getFirstNameLetter(name: string): string {
        if (!name) return '?';
        return name.charAt(0).toUpperCase();
    }

    private getAvatarColor(name: string): string {
        if (!name) return '#6B7280';
        
        const colors = [
            '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
            '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
        ];
        
        const charCode = name.charCodeAt(0);
        return colors[charCode % colors.length];
    }

    private getRoleBadgeClass(role: string): string {
        switch(role) {
            case 'super-admin': return 'bg-purple-100 text-purple-800';
            case 'admin': return 'bg-blue-100 text-blue-800';
            case 'manager': return 'bg-yellow-100 text-yellow-800';
            case 'player': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    }

    private getRoleDisplayName(role: string): string {
        switch(role) {
            case 'super-admin': return 'Super Admin';
            case 'admin': return 'Admin';
            case 'manager': return 'Manager';
            case 'player': return 'Player';
            default: return role;
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
    
    getTimeFromDate(dateString: string): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * Edit user (opens dialog)
     */
    editUser(user: any, event: Event): void {
        event.stopPropagation();
        
        const dialogRef = this.dialog.open(EditUserDialogComponent, {
            width: '500px',
            data: { user },
            disableClose: false,
            autoFocus: false,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadCurrentView();
            }
        });
    }

    /**
     * Delete user as super admin
     */
    // Update just the deleteUser method in the users-details.component.ts:

/**
 * Delete user as super admin
 */
deleteUser(user: any, event: Event): void {
    event.stopPropagation();
    
    const confirmation = window.confirm(`Are you sure you want to delete user "${user.name}"? This action cannot be undone.`);
    
    if (confirmation) {
        this.teamsService.deleteUserAsSuperAdmin(user._id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this.snackBar.open('User deleted successfully', 'Close', { duration: 3000 });
                    this.loadCurrentView();
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error deleting user:', err);
                    // Show the actual error message from the API
                    this.snackBar.open(err.message || 'Error deleting user', 'Close', { duration: 3000 });
                    this.cdr.markForCheck();
                }
            });
    }
}
}
